import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import customFetch from '@utils/customFetch';
import { toast } from 'react-toastify';
import { SUBSCRIPTION_STATUS, COMPANY_STATUS } from '@utils/constants';
import NewCompanySlideIn from './NewCompanySlideIn';

const allCompaniesQuery = {
  queryKey: ['allCompanies'],
  queryFn: async () => {
    const { data } = await customFetch.get('/companies');
    return data;
  },
};

export const loader = (queryClient) => async () => {
  await queryClient.ensureQueryData(allCompaniesQuery);
  return null;
};

const statusLabels = {
  [SUBSCRIPTION_STATUS.ACTIVE]: 'Aktiv',
  [SUBSCRIPTION_STATUS.PAST_DUE]: 'Überfällig',
  [SUBSCRIPTION_STATUS.CANCELED]: 'Gekündigt',
  [SUBSCRIPTION_STATUS.TRIALING]: 'Testphase',
  [SUBSCRIPTION_STATUS.UNPAID]: 'Unbezahlt',
};

const statusColors = {
  [SUBSCRIPTION_STATUS.ACTIVE]: 'bg-green-50 text-green-700 ring-green-600/20',
  [SUBSCRIPTION_STATUS.PAST_DUE]:
    'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
  [SUBSCRIPTION_STATUS.CANCELED]: 'bg-red-50 text-red-700 ring-red-600/20',
  [SUBSCRIPTION_STATUS.TRIALING]: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  [SUBSCRIPTION_STATUS.UNPAID]: 'bg-red-50 text-red-700 ring-red-600/20',
};

const companyStatusLabels = {
  [COMPANY_STATUS.ACTIVE]: 'Aktiv',
  [COMPANY_STATUS.SUSPENDED]: 'Gesperrt',
  [COMPANY_STATUS.PENDING]: 'Ausstehend',
};

const companyStatusColors = {
  [COMPANY_STATUS.ACTIVE]: 'bg-green-50 text-green-700 ring-green-600/20',
  [COMPANY_STATUS.SUSPENDED]: 'bg-red-50 text-red-700 ring-red-600/20',
  [COMPANY_STATUS.PENDING]: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
};

const CompanyList = () => {
  const { data } = useQuery(allCompaniesQuery);
  const companies = data?.companies || [];

  const [showSlideIn, setShowSlideIn] = useState(false);
  const queryClient = useQueryClient();

  const handleCreateCompany = async (formData) => {
    const payload = Object.fromEntries(formData);
    try {
      await customFetch.post('/companies', payload);
      queryClient.invalidateQueries(['allCompanies']);
      toast.success('Firma erfolgreich erstellt!');
      setShowSlideIn(false);
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Erstellen');
    }
  };

  return (
    <>
      <NewCompanySlideIn
        showSlideIn={showSlideIn}
        setShowSlideIn={setShowSlideIn}
        onSubmit={handleCreateCompany}
      />

      <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold leading-6 text-gray-900">
              Firmenverwaltung
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Hier sind alle Firmen aufgelistet.
            </p>
          </div>
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <button
              type="button"
              className="block rounded-md bg-svisit-primary-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
              onClick={() => setShowSlideIn(true)}
            >
              Firma hinzufügen
            </button>
          </div>
        </div>
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3">
                      Firmenname
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Abo-Status
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Benutzer
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      E-Mail
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Rabatt
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Erstellt am
                    </th>
                    <th className="relative py-3.5 pl-3 pr-4 sm:pr-3">
                      <span className="sr-only">Bearbeiten</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {companies.map((company) => (
                    <tr key={company._id} className="even:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3">
                        {company.name}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                            companyStatusColors[company.status] ||
                            'bg-gray-50 text-gray-700 ring-gray-600/20'
                          }`}
                        >
                          {companyStatusLabels[company.status] || company.status}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                            statusColors[company.subscriptionStatus] ||
                            'bg-gray-50 text-gray-700 ring-gray-600/20'
                          }`}
                        >
                          {statusLabels[company.subscriptionStatus] ||
                            company.subscriptionStatus ||
                            '–'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {company.paidUsers || 0}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {company.email}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {company.discountNote ? (
                          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                            Rabatt
                          </span>
                        ) : (
                          '–'
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {new Date(company.createdAt).toLocaleDateString('de-CH')}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-3">
                        <a
                          href={`/dashboard/firmenverwaltung/${company._id}`}
                          className="text-svisit-primary-600 hover:text-svisit-primary-900"
                        >
                          Bearbeiten
                          <span className="sr-only">, {company.name}</span>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyList;
